import {
  APP_INITIALIZER,
  ApplicationRef,
  DoBootstrap,
  ErrorHandler,
  inject,
  LOCALE_ID,
  NgModule,
} from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';

import { Router } from '@angular/router';
import { FormlyModule } from '@ngx-formly/core';
import * as Sentry from '@sentry/angular';
import { Amplify } from '@aws-amplify/core';
import { NgxMaskModule } from 'ngx-mask';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { generateAwsConfig } from 'src/aws-config';

import { AppRoutingModule } from '@app/app-routing.module';
import { AppComponent } from '@app/app.component';
import { TravelTimePipe } from '@app/pipes/travel-time.pipe';
import { HeaderComponent } from '@app/components/header/header.component';
import { FooterComponent } from '@app/components/footer/footer.component';
import { FlightInfoComponent } from '@app/components/flight-info/flight-info.component';
import { SearchingForATravelExpertComponent } from '@app/components/searching-for-a-travel-expert/searching-for-a-travel-expert.component';
import { ReviewsComponent } from '@app/components/reviews/reviews.component';
import { GetFirstNamePipe } from '@app/pipes/get-first-name.pipe';
import { OptionComponent } from '@app/components/option/option.component';
import { OptionItineraryComponent } from '@app/components/option-itinerary/option-itinerary.component';
import { SaveBadgeComponent } from '@app/components/badges/save-badge/save-badge.component';
import { BookingStatusComponent } from '@app/components/booking-status/booking-status.component';
import { OptionDetailsComponent } from '@app/components/option-details/option-details.component';
import { BonusFlightComponent } from '@app/components/bonus-flight/bonus-flight.component';
import { ExtraLegComponent } from '@app/components/extra-leg/extra-leg.component';
import { DaysShiftAfterLayoverPipe } from '@app/pipes/days-shift-after-layover.pipe';
import { HasDaysShiftAfterLayoverPipe } from '@app/pipes/has-days-shift-after-layover.pipe';
import { PassengersFormComponent } from '@app/forms/passengers-form/passengers-form.component';
import { InputFieldComponent } from '@app/forms/formly/types/input-field/input-field.component';
import { FieldWrapperComponent } from '@app/forms/formly/wrappers/field-wrapper/field-wrapper.component';
import { TravelerCardsComponent } from '@app/forms/formly/types/traveler-cards/traveler-cards.component';
import { formlyConfig } from '@app/forms/formly/formly-config';
import { DateFieldComponent } from '@app/forms/formly/types/date-field/date-field.component';
import { FormErrorComponent } from '@app/forms/formly/form-error/form-error.component';
import { RadioFieldComponent } from '@app/forms/formly/types/radio-field/radio-field.component';
import { GoBackConfirmComponent } from '@app/components/modals/go-back-confirm/go-back-confirm.component';
import { NotFoundPageComponent } from '@app/pages/not-found-page/not-found-page.component';
import { ErrorModalComponent } from '@app/components/modals/error-modal/error-modal.component';
import { PricesTooltipComponent } from '@app/components/tooltips/prices-tooltip/prices-tooltip.component';
import { ExtractNodesPipe } from '@app/ui/pipes/extract-nodes.pipe';
import { WithoutTimezonePipe } from '@app/ui/pipes/without-timezone.pipe';
import { PortalCurrencyPipe } from '@app/pipes/portal-currency.pipe';
import { LabelPipe } from '@app/ui/pipes/label.pipe';
import { HiddenCityTooltipComponent } from '@app/components/tooltips/hidden-city-tooltip/hidden-city-tooltip.component';
import { ThrowawayTicketingTooltipComponent } from '@app/components/tooltips/throwaway-ticketing-tooltip/throwaway-ticketing-tooltip.component';
import { HotelBookingsComponent } from '@app/components/hotel-bookings/hotel-bookings.component';
import { HotelBookingsTooltipComponent } from '@app/components/tooltips/hotel-bookings-tooltip/hotel-bookings-tooltip.component';
import { HasDaysShiftPipe } from '@app/pipes/has-days-shift.pipe';
import { DropdownFieldComponent } from '@app/forms/formly/types/dropdown-field/dropdown-field.component';
import { PhonePipe } from '@app/ui/pipes/phone.pipe';
import { TermOfUseComponent } from '@app/components/term-of-use/term-of-use.component';
import { PassengersPreviewComponent } from '@app/components/passengers-preview/passengers-preview.component';
import { CardFormComponent } from '@app/forms/card-form/card-form.component';
import { CcNumberFieldComponent } from '@app/forms/formly/types/cc-number-field/cc-number-field.component';
import { CcExpirationFieldComponent } from '@app/forms/formly/types/cc-expiration-field/cc-expiration-field.component';
import { ProtectionSelectComponent } from '@app/components/protection-select/protection-select.component';
import { StepperComponent } from '@app/components/stepper/stepper.component';
import { CcCvvFieldComponent } from '@app/forms/formly/types/cc-cvv-field/cc-cvv-field.component';
import { PriceSummaryComponent } from '@app/components/price-summary/price-summary.component';
import { GetCountryByIdPipe } from '@app/pipes/get-country-by-id.pipe';
import { HiddenFlightsModalComponent } from '@app/components/modals/hidden-flights-modal/hidden-flights-modal.component';
import { AskToSignInModalComponent } from '@app/components/modals/ask-to-sign-in-modal/ask-to-sign-in-modal.component';
import { SignInModalComponent } from '@app/components/modals/sign-in-modal/sign-in-modal.component';
import { LoginPageComponent } from '@app/pages/login-page/login-page.component';
import { LogoutPageComponent } from '@app/pages/logout-page/logout-page.component';
import { VerificationModalComponent } from '@app/components/modals/verification-modal/verification-modal.component';
import { OldLeadPageComponent } from '@app/pages/old-lead-page/old-lead-page.component';
import { LeadPageComponent } from '@app/pages/lead-page/lead-page.component';
import { PassengersPageComponent } from '@app/pages/passengers-page/passengers-page.component';
import { LeadWrapperPageComponent } from '@app/pages/lead-wrapper-page/lead-wrapper-page.component';
import { LeadLoadingPageComponent } from '@app/pages/lead-loading-page/lead-loading-page.component';
import { ThankYouPageComponent } from '@app/pages/thank-you-page/thank-you-page.component';
import { GratitudePageComponent } from '@app/pages/gratitude-page/gratitude-page.component';
import { OverviewAndPaymentPageComponent } from '@app/pages/overview-and-payment-page/overview-and-payment-page.component';
import { FormatMsPipe } from '@app/ui/pipes/format-ms.pipe';
import { ContinueWithEmailModalComponent } from '@app/components/modals/continue-with-email-modal/continue-with-email-modal.component';
import { ActivationCodeModalComponent } from '@app/components/modals/activation-code-modal/activation-code-modal.component';
import { PasscodeModalComponent } from '@app/components/modals/passcode-modal/passcode-modal.component';
import { TravelExpertInfoComponent } from '@app/components/travel-expert-info/travel-expert-info.component';
import { HomePageComponent } from '@app/pages/home-page/home-page.component';
import { TripItemComponent } from '@app/components/trip-item/trip-item.component';
import { InfoBlockFieldComponent } from '@app/forms/formly/types/info-block-field/info-block-field.component';
import { CurrencyFieldComponent } from '@app/forms/formly/types/currency-field/currency-field.component';
import { PhoneFieldComponent } from '@app/forms/formly/types/phone-field/phone-field.component';
import { OptionsListComponent } from '@app/components/options-list/options-list.component';
import { CardWrapperComponent } from '@app/forms/formly/wrappers/card-wrapper/card-wrapper.component';
import { GetMaxBalanceAmountPipe } from '@app/pipes/get-max-balance-amount.pipe';
import { GetTotalAmountPipe } from '@app/pipes/get-total-amount.pipe';
import { ProductBadgeComponent } from '@app/components/badges/product-badge/product-badge.component';
import { CustomFieldComponent } from '@app/forms/formly/types/custom-field/custom-field.component';
import { CheckboxFieldComponent } from '@app/forms/formly/types/checkbox-field/checkbox-field.component';
import { StripeFormComponent } from '@app/forms/stripe-form/stripe-form.component';
import { PromocodeComponent } from '@app/components/promocode/promocode.component';
import { StartPlanningCardComponent } from '@app/components/start-planning-card/start-planning-card.component';
import { NeedHelpCardComponent } from '@app/components/need-help-card/need-help-card.component';
import { PlanningTripComponent } from '@app/forms/formly/types/planning-trip/planning-trip.component';
import { PlanningTripModalComponent } from '@app/components/modals/planning-trip-modal/planning-trip-modal.component';
import { InputFieldWithAutocompleteComponent } from '@app/forms/formly/types/input-field-with-autocomplete/input-field-with-autocomplete.component';
import { InputFieldWithAutocompleteMobileViewComponent } from '@app/forms/formly/types/input-field-with-autocomplete/input-field-with-autocomplete-mobile-view/input-field-with-autocomplete-mobile-view.component';
import { InputFieldWithAutocompleteOptionComponent } from '@app/forms/formly/types/input-field-with-autocomplete/input-field-with-autocomplete-option/input-field-with-autocomplete-option.component';
import { CustomVirtualScrollDirective } from '@app/ui/directives/custom-virtual-scroll.directive';
import { RangePickerDropdownComponent } from '@app/forms/formly/types/range-picker-dropdown/range-picker-dropdown.component';
import { CashbackBadgeComponent } from '@app/components/badges/cashback-badge/cashback-badge.component';
import { LayoverBadgeComponent } from '@app/components/badges/layover-badge/layover-badge.component';
import { NonstopFlightBadgeComponent } from '@app/components/badges/nonstop-flight-badge/nonstop-flight-badge.component';
import { GetUniqAirlinesLogosPipe } from '@app/pipes/get-uniq-airlines-logos.pipe';
import { ServiceClassBadgeComponent } from '@app/components/badges/service-class-badge/service-class-badge.component';
import { ExpertsChoiceBadgeComponent } from '@app/components/badges/experts-choice-badge/experts-choice-badge.component';
import { FlightBadgeComponent } from '@app/components/badges/flight-badge/flight-badge.component';
import { ProtectionPlansComponent } from '@app/components/protection-plans/protection-plans.component';
import { OptionPageComponent } from '@app/pages/option-page/option-page.component';
import { FastestOptionBadgeComponent } from '@app/components/badges/fastest-option-badge/fastest-option-badge.component';
import { TrustpilotBadgeComponent } from '@app/components/badges/trustpilot-badge/trustpilot-badge.component';
import { RiskFreeBadgeComponent } from '@app/components/badges/risk-free-badge/risk-free-badge.component';
import { HomeLoadingPageComponent } from '@app/pages/home-loading-page/home-loading-page.component';
import { LeadDetailsSubtitleComponent } from '@app/components/lead-details-subtitle/lead-details-subtitle.component';
import { OptionDateChangesComponent } from '@app/components/option-date-changes/option-date-changes.component';
import { EmptyLeadComponent } from '@app/components/empty-lead/empty-lead.component';
import { LeadDetailsTitleComponent } from '@app/components/lead-details-title/lead-details-title.component';
import { TripPageComponent } from '@app/pages/trip-page/trip-page.component';
import { TripUpsellBannerComponent } from '@app/components/trip-upsell-banner/trip-upsell-banner.component';
import { BookingTripCardComponent } from '@app/components/booking-trip-card/booking-trip-card.component';
import { PaymentInfoComponent } from '@app/components/payment-info/payment-info.component';
import { HotelBookingsBadgeComponent } from '@app/components/badges/hotel-bookings-badge/hotel-bookings-badge.component';
import { AssetService } from '@app/services/assets/assets.service';
import { CommonDataService } from '@app/services/common-data/common-data.service';
import { LogosContainerComponent } from '@app/components/logos-container/logos-container.component';
import { BookingLoadingPageComponent } from '@app/pages/booking-loading-page/booking-loading-page.component';
import { BookingHotelCardComponent } from '@app/components/booking-hotel-card/booking-hotel-card.component';
import { BookingPageComponent } from '@app/pages/booking-page/booking-page.component';
import { TravelerInfoComponent } from '@app/components/traveler-info/traveler-info.component';
import { BookingTripComponent } from '@app/pages/booking-page/booking-trip/booking-trip.component';
import { BookingHotelComponent } from '@app/pages/booking-page/booking-hotel/booking-hotel.component';
import { LeastExpensiveBadgeComponent } from '@app/components/badges/least-expensive-badge/least-expensive-badge.component';
import { LearnMoreComponent } from '@app/components/learn-more/learn-more.component';
import {
  BorderWrapperComponent,
  ButtonComponent,
  CardComponent,
  CardCvvInputComponent,
  CardExpirationInputComponent,
  CardNumberInputComponent,
  CheckboxComponent,
  ChipComponent,
  ClickOutsideDirective,
  CopyToClipboardDirective,
  CurrencyInputComponent,
  DotSeparatorComponent,
  DropdownComponent,
  DsInputFieldDirective,
  IconComponent,
  InfoBlockComponent,
  InputComponent,
  ModalComponent,
  ModalDirective,
  OtpInputComponent,
  PhoneInputComponent,
  PreventTouchmoveDirective,
  FeaturedBadgeComponent,
  MetaBadgeComponent,
  PromoBadgeComponent,
  RadioComponent,
  RadioToggleComponent,
  SkeletonComponent,
  StepperComponent as DsStepperComponent,
  TabsComponent,
  TooltipComponent,
  TooltipDirective,
} from '@voyagu/portal-design-system';
import { AncillaryBundlePageComponent } from '@app/pages/ancillary-bundle-page/ancillary-bundle-page.component';
import { ClientReviewComponent } from '@app/components/client-review/client-review.component';
import { AncillaryBundleItemComponent } from '@app/pages/ancillary-bundle-page/item/ancillary-bundle-item.component';
import { AncillaryStatusComponent } from '@app/pages/ancillary-bundle-page/status/ancillary-status.component';
import { AncillaryAllBenefitsComponent } from '@app/pages/ancillary-bundle-page/all-benefits/ancillary-all-benefits.component';
import { OptionFullBadgeComponent } from '@app/components/badges/option-full-badge/option-full-badge.component';
import { RadioToggleButtonComponent } from '@app/ui/components/radio-toggle-button/radio-toggle-button.component';
import { ExtractServiceClassesPipe } from '@app/pipes/extract-service-classes.pipe';
import { OptionDateChangesPipe } from '@app/pipes/option-date-changes.pipe';
import { ClientTravelersPageComponent } from '@app/pages/client-travelers-page/client-travelers-page.component';
import { TripsPageComponent } from '@app/pages/trips-page/trips-page.component';
import { TripsCardComponent } from '@app/pages/trips-page/trips-card/trips-card.component';
import { ClientTravelerPageComponent } from '@app/pages/client-traveler-page/client-traveler-page.component';
import { ClientTravelerFormComponent } from '@app/forms/client-traveler-form/client-traveler-form.component';
import { FoldCardWrapperComponent } from '@app/forms/formly/wrappers/fold-card-wrapper/fold-card-wrapper.component';
import { AncillaryUpsellPageComponent } from '@app/pages/ancillary-upsell-page/ancillary-upsell-page.component';
import { CompletePageComponent } from '@app/pages/complete-page/complete-page.component';
import { UpsellInfoBadgeComponent } from '@app/components/badges/upsell-info-badge/upsell-info-badge.component';
import { UpsellDetailsComponent } from '@app/components/upsell-details/upsell-details.component';
import { ErrorPageComponent } from '@app/pages/error-page/error-page.component';
import { TravelExpertInfoQuickFormComponent } from '@app/components/travel-expert-info-quick-form/travel-expert-info-quick-form.component';
import { QuickOptionItineraryComponent } from '@app/components/quick-option-itinerary/quick-option-itinerary.component';
import { DurationPipe } from '@app/pipes/duration.pipe';
import { GetAirportByLocationCodePipe } from '@app/pipes/get-airport-by-location-code.pipe';
import { ShortDateComponent } from '@app/forms/formly/types/short-date/short-date.component';
import { RepeatComponent } from '@app/forms/formly/types/repeat/repeat.component';
import { WalletBalanceComponent } from '@app/components/wallet-balance/wallet-balance.component';
import { DisruptionProtectionPageComponent } from '@app/pages/disruption-protection-page/disruption-protection-page.component';
import { CancelForAnyReasonPageComponent } from '@app/pages/cancel-for-any-reason-page/cancel-for-any-reason-page.component';
import { AncillariesBundleModalComponent } from '@app/components/modals/ancillaries-bundle-modal/ancillaries-bundle-modal.component';
import { DisruptionProtectionModalComponent } from '@app/components/modals/disruption-protection-modal/disruption-protection-modal.component';
import { CfarUntilAtPipe } from '@app/pipes/cfar-until-at.pipe';
import { GetCoveragePipe } from '@app/pipes/get-coverage.pipe';
import { TiersPageComponent } from '@app/pages/tiers-page/tiers-page.component';
import { TierChangePageComponent } from '@app/pages/tier-change-page/tier-change-page.component';
import { UpsellOptionModalComponent } from '@app/components/modals/upsell-option-modal/upsell-option-modal.component';
import { MostPopuparBadgeComponent } from '@app/components/badges/most-popupar-badge/most-popupar-badge.component';
import { QuoteLayoutPageComponent } from '@app/pages/quote-layout-page/quote-layout-page.component';
import { QuoteLoadingPageComponent } from '@app/pages/quote-loading-page/quote-loading-page.component';
import { QuotePageComponent } from '@app/pages/quote-page/quote-page.component';
import { QuoteExpiredPageComponent } from '@app/pages/quote-expired-page/quote-expired-page.component';
import { QuoteThankYouPageComponent } from '@app/pages/quote-thank-you-page/quote-thank-you-page.component';
import { ReferralsPageComponent } from '@app/pages/referrals-page/referrals-page.component';
import { ReferralRequestPageComponent } from '@app/pages/referral-request-page/referral-request-page.component';
import { ReferralCreditBannerComponent } from '@app/components/referral-credit-banner/referral-credit-banner.component';
import { ReferralFaqSectionComponent } from '@app/components/referral-faq-section/referral-faq-section.component';
import { ReferralProgramHeroComponent } from '@app/components/referral-program-hero/referral-program-hero.component';
import { ReferralHomeBannerComponent } from '@app/components/referral-home-banner/referral-home-banner.component';
import { ReferralHowItWorksComponent } from '@app/components/referral-how-it-works/referral-how-it-works.component';
import { ReferralDetailModalComponent } from '@app/components/referral-detail-modal/referral-detail-modal.component';
import { ReferralCreditNotAppliedBannerComponent } from '@app/components/referral-credit-not-applied-banner/referral-credit-not-applied-banner.component';
import { authTokenInterceptor } from '@app/interceptors/auth-token.interceptor';
import { passcodeTokenInterceptor } from '@app/interceptors/passcode-token.interceptor';
import { authErrorInterceptor } from '@app/interceptors/auth-error.interceptor';
import { sentryHttpInterceptor } from '@app/interceptors/sentry-http.interceptor';
import { AgentPreviewAuthService } from '@app/services/auth/agent-preview-auth.service';

Amplify.configure(generateAwsConfig());

const sentryProviders = [
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
      showDialog: false,
    }),
  },
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  {
    provide: APP_INITIALIZER,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    useFactory: () => () => {},
    deps: [Sentry.TraceService],
    multi: true,
  },
  {
    provide: LOCALE_ID,
    useValue: 'en-US',
  },
];

@NgModule({
  declarations: [
    AncillaryBundleItemComponent,
    AncillaryStatusComponent,
    AncillaryAllBenefitsComponent,
    ClientReviewComponent,
    CustomVirtualScrollDirective,
    AppComponent,
    ExtractNodesPipe,
    TravelTimePipe,
    WithoutTimezonePipe,
    NotFoundPageComponent,
    BookingPageComponent,
    BookingTripComponent,
    BookingHotelComponent,
    TravelerInfoComponent,
    TripPageComponent,
    TripUpsellBannerComponent,
    PortalCurrencyPipe,
    ErrorModalComponent,
    LabelPipe,
    HeaderComponent,
    FooterComponent,
    FlightInfoComponent,
    SearchingForATravelExpertComponent,
    ReviewsComponent,
    GetFirstNamePipe,
    OptionComponent,
    OptionDateChangesComponent,
    OptionItineraryComponent,
    SaveBadgeComponent,
    BookingStatusComponent,
    OptionDetailsComponent,
    BonusFlightComponent,
    ExtraLegComponent,
    PricesTooltipComponent,
    FlightBadgeComponent,
    DaysShiftAfterLayoverPipe,
    HasDaysShiftAfterLayoverPipe,
    PassengersFormComponent,
    InputFieldComponent,
    PhoneFieldComponent,
    InputFieldWithAutocompleteComponent,
    InputFieldWithAutocompleteMobileViewComponent,
    InputFieldWithAutocompleteOptionComponent,
    FieldWrapperComponent,
    TravelerCardsComponent,
    RadioToggleButtonComponent,
    DateFieldComponent,
    FormErrorComponent,
    RadioFieldComponent,
    GoBackConfirmComponent,
    HiddenCityTooltipComponent,
    ThrowawayTicketingTooltipComponent,
    HotelBookingsComponent,
    HotelBookingsTooltipComponent,
    HasDaysShiftPipe,
    DropdownFieldComponent,
    PhonePipe,
    TermOfUseComponent,
    PassengersPreviewComponent,
    ProtectionPlansComponent,
    CardFormComponent,
    StripeFormComponent,
    CcNumberFieldComponent,
    CcExpirationFieldComponent,
    ProtectionSelectComponent,
    StepperComponent,
    CcCvvFieldComponent,
    GetCountryByIdPipe,
    HiddenFlightsModalComponent,
    AskToSignInModalComponent,
    SignInModalComponent,
    LoginPageComponent,
    LogoutPageComponent,
    VerificationModalComponent,
    PlanningTripComponent,
    PlanningTripModalComponent,
    OldLeadPageComponent,
    LeadPageComponent,
    PassengersPageComponent,
    LeadWrapperPageComponent,
    LeadLoadingPageComponent,
    BookingLoadingPageComponent,
    HomeLoadingPageComponent,
    ThankYouPageComponent,
    AncillaryBundlePageComponent,
    GratitudePageComponent,
    OverviewAndPaymentPageComponent,
    FormatMsPipe,
    ContinueWithEmailModalComponent,
    ActivationCodeModalComponent,
    PasscodeModalComponent,
    TravelExpertInfoComponent,
    HomePageComponent,
    TripItemComponent,
    InfoBlockFieldComponent,
    CurrencyFieldComponent,
    OptionsListComponent,
    CardWrapperComponent,
    GetMaxBalanceAmountPipe,
    GetTotalAmountPipe,
    ProductBadgeComponent,
    CustomFieldComponent,
    CheckboxFieldComponent,
    PromocodeComponent,
    StartPlanningCardComponent,
    NeedHelpCardComponent,
    CashbackBadgeComponent,
    NonstopFlightBadgeComponent,
    LayoverBadgeComponent,
    GetUniqAirlinesLogosPipe,
    ServiceClassBadgeComponent,
    ExpertsChoiceBadgeComponent,
    OptionFullBadgeComponent,
    OptionPageComponent,
    FastestOptionBadgeComponent,
    TrustpilotBadgeComponent,
    RiskFreeBadgeComponent,
    LeadDetailsSubtitleComponent,
    LeadDetailsTitleComponent,
    LogosContainerComponent,
    EmptyLeadComponent,
    BookingTripCardComponent,
    BookingHotelCardComponent,
    PaymentInfoComponent,
    HotelBookingsBadgeComponent,
    LeastExpensiveBadgeComponent,
    ClientTravelersPageComponent,
    TripsPageComponent,
    TripsCardComponent,
    ClientTravelerPageComponent,
    ClientTravelerFormComponent,
    FoldCardWrapperComponent,
    AncillaryUpsellPageComponent,
    CompletePageComponent,
    UpsellInfoBadgeComponent,
    UpsellDetailsComponent,
    ErrorPageComponent,
    TravelExpertInfoQuickFormComponent,
    QuickOptionItineraryComponent,
    DurationPipe,
    GetAirportByLocationCodePipe,
    ShortDateComponent,
    RepeatComponent,
    WalletBalanceComponent,
    DisruptionProtectionPageComponent,
    CancelForAnyReasonPageComponent,
    AncillariesBundleModalComponent,
    DisruptionProtectionModalComponent,
    CfarUntilAtPipe,
    GetCoveragePipe,
    TiersPageComponent,
    TierChangePageComponent,
    UpsellOptionModalComponent,
    MostPopuparBadgeComponent,
    QuoteLayoutPageComponent,
    QuoteLoadingPageComponent,
    QuotePageComponent,
    QuoteExpiredPageComponent,
    QuoteThankYouPageComponent,
    ReferralsPageComponent,
    ReferralRequestPageComponent,
    ReferralCreditBannerComponent,
    ReferralFaqSectionComponent,
    ReferralProgramHeroComponent,
    ReferralHomeBannerComponent,
    ReferralHowItWorksComponent,
    ReferralDetailModalComponent,
    ReferralCreditNotAppliedBannerComponent,
  ],
  imports: [
    BrowserAnimationsModule,
    RangePickerDropdownComponent,
    OverlayModule,
    PortalModule,
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule.forRoot(formlyConfig),
    NgxMaskModule.forRoot(),
    LearnMoreComponent,
    ButtonComponent,
    InputComponent,
    IconComponent,
    DsInputFieldDirective,
    PhoneInputComponent,
    CardNumberInputComponent,
    CardExpirationInputComponent,
    CardCvvInputComponent,
    CurrencyInputComponent,
    OtpInputComponent,
    CheckboxComponent,
    RadioComponent,
    RadioToggleComponent,
    PromoBadgeComponent,
    MetaBadgeComponent,
    FeaturedBadgeComponent,
    DsStepperComponent,
    BorderWrapperComponent,
    CardComponent,
    ChipComponent,
    ClickOutsideDirective,
    CopyToClipboardDirective,
    DotSeparatorComponent,
    DropdownComponent,
    InfoBlockComponent,
    ModalComponent,
    ModalDirective,
    PreventTouchmoveDirective,
    SkeletonComponent,
    TabsComponent,
    TooltipComponent,
    TooltipDirective,
    PriceSummaryComponent,
    ExtractServiceClassesPipe,
    OptionDateChangesPipe,
  ],
  providers: [
    DatePipe,
    CurrencyPipe,
    DaysShiftAfterLayoverPipe,
    PortalCurrencyPipe,
    GetMaxBalanceAmountPipe,
    CfarUntilAtPipe,
    GetCoveragePipe,
    ...sentryProviders,
    provideHttpClient(
      withInterceptors([
        authTokenInterceptor as unknown as HttpInterceptorFn,
        passcodeTokenInterceptor as unknown as HttpInterceptorFn,
        authErrorInterceptor as unknown as HttpInterceptorFn,
        sentryHttpInterceptor as unknown as HttpInterceptorFn,
      ]),
    ),
  ],
})
export class AppModule implements DoBootstrap {
  private assetService = inject(AssetService);
  private commonDataService = inject(CommonDataService);
  private agentPreviewAuth = inject(AgentPreviewAuthService);

  ngDoBootstrap(appRef: ApplicationRef): void {
    this.agentPreviewAuth.initFromUrl();

    this.assetService.loadDataJson(window.location.origin).subscribe((data) => {
      const link = document.createElement('link');
      const theme = data.theme ?? 'theme';

      if (theme) {
        link.rel = 'stylesheet';
        link.href = `assets/styles/${theme}.css`;
        document.head.appendChild(link);
      }

      if (data.dataTheme) {
        document.documentElement.setAttribute('data-theme', data.dataTheme);
      }

      this.setFavicon(data.favicon);
      this.commonDataService.setData(data);
      appRef.bootstrap(AppComponent);
    });
  }

  private setFavicon(iconUrl: string): void {
    const link: HTMLLinkElement = document.createElement('link');
    link.rel = 'icon';
    link.href = `assets/favicons/${iconUrl}`;
    link.type = 'image/x-icon';

    const head = document.head;
    const existingLink = head.querySelector("link[rel*='icon']");
    if (existingLink) {
      head.removeChild(existingLink);
    }

    head.appendChild(link);
  }
}
